import { Button, Card, CardActions, Collapse } from "@mui/material";
import { useContext } from "react";
import { useTranslation } from "react-i18next";
import PolygonSummaryRow from "./polygon-summary-row";
import PolygonDetailContent from "./polygon-detail-content";
import styles from "./polygon-result.module.scss";
import {
  PolygonStatisticsCardProps,
  PolygonMetricBundle,
  PolygonMetricValues
} from "./types";
import { AppAction, AppContext } from "helpers/context";

const toDisplayNumber = (value: number, digits: number) => {
  return String(parseFloat(value.toFixed(digits)));
};

const formatNumber = (value?: number) => {
  const safeValue = Number(value || 0);
  return toDisplayNumber(safeValue, 4);
};

const formatPercent = (value?: number) => {
  const safeValue = Number(value || 0) * 100;
  return `${toDisplayNumber(safeValue, 2)}%`;
};

const formatCoordinatePair = (
  longitude?: number,
  latitude?: number,
  formatNumberDigits = 8
) => {
  if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) {
    return "-";
  }

  return `${toDisplayNumber(Number(longitude), formatNumberDigits)}, ${toDisplayNumber(
    Number(latitude), formatNumberDigits
  )}`;
};

const resolveMetrics = (
  metrics: PolygonMetricValues | PolygonMetricBundle | undefined
) => {
  if (!metrics) {
    return {
      summaryMetrics: undefined,
      detailMetrics: undefined
    };
  }

  const bundle = metrics as PolygonMetricBundle;
  const hasBundledMetrics = !!bundle.intensityMetrics || !!bundle.detailMetrics;

  if (!hasBundledMetrics) {
    return {
      summaryMetrics: metrics as PolygonMetricValues,
      detailMetrics: metrics as PolygonMetricValues
    };
  }

  return {
    summaryMetrics: bundle.intensityMetrics,
    detailMetrics: bundle.detailMetrics
  };
};



function PolygonStatisticsCard({
  item,
  legendInfo,
  isExpanded,
  onToggle,
  onOpenFathom,
}: PolygonStatisticsCardProps) {
  const [t] = useTranslation();
  const [, dispatch] = useContext(AppContext);

  const { summaryMetrics, detailMetrics } = resolveMetrics(item.metrics);
  const maxValuePoints = item.maxValuePoints || [];
  const maxValuePoint = maxValuePoints[0];

  const handleCoordinateClick = () => {
    const longitude = Number(maxValuePoint?.longitude);
    const latitude = Number(maxValuePoint?.latitude);
    if (Number.isNaN(longitude) || Number.isNaN(latitude)) return;

    dispatch({
      type: AppAction.setMapClickEvent,
      payload: {
        lnglat: {
          getLng: () => longitude,
          getLat: () => latitude
        }
      }
    });
  };
  return (
    <Card key={item.layerId} variant="outlined" className={styles.statCard}>
      <PolygonSummaryRow
        item={item}
        legendInfo={legendInfo}
        metrics={summaryMetrics}
        onOpenFathom={onOpenFathom}
        formatNumber={formatNumber}
        isExpanded={isExpanded}
      />

      <Collapse className={styles.cardCollapse} in={isExpanded} timeout="auto" unmountOnExit>
        <PolygonDetailContent
          item={item}
          legendInfo={legendInfo}
          metrics={detailMetrics}
          intensityMetrics={summaryMetrics}
          maxValuePoints={maxValuePoints}
          maxValuePoint={maxValuePoint}
          formatPercent={formatPercent}
          formatCoordinatePair={formatCoordinatePair}
          onCoordinateClick={handleCoordinateClick}
        />
      </Collapse>

      <CardActions>
        <Button color="success" size="small" onClick={() => onToggle(item.layerId)}>
          {isExpanded ? t("buttons.collapse") : t("buttons.expand")}
        </Button>
      </CardActions>
    </Card>
  );
}

export default PolygonStatisticsCard;
